import Booking from "../models/Booking.model.js"
import BookingData from "../models/BookingData.model.js"
import BookingPreset from "../models/BookingPreset.model.js"
import Collection from "../models/Collection.model.js"
import Service from "../models/Service.model.js"
import _ from "lodash"

// test: jest src/presenters/__tests__/Booking.presenter.spec.js
export default class BookingPresenter {
  constructor(booking, bookingPreset, collectionsForService) {
    this.booking = booking
    this.bookingPreset = bookingPreset
    this.collectionsForService = collectionsForService
  }

  static create(booking, bookingPreset, collectionsForService) {
    return new BookingPresenter(booking, bookingPreset, collectionsForService)
  }

  get fieldDataInString() {
    return this.booking.data.reduce((acc, item) => {
      acc[item.field_id] = this.parseValueToString(item.field_id)
      return { ...acc}
    }, {})
  }

  get branchName() {
    return this.booking.branch?.name || this.booking.branch_id
  }

  get staffName() {
    return this.booking.staff?.name || this.booking.staff_id
  }

  get selectedServices() {
    const bookingModel = new Booking(this.booking)
    const bookingPresetModel = new BookingPreset(this.bookingPreset)
    return _.get(this.collectionsForService.find(collection => collection.id === bookingPresetModel.collectionIdForService), 'items', [])
      .map(item => Service.fromCollectionItem(item, bookingPresetModel.serviceFieldMappings))
      .filter(service => bookingModel.serviceIdInArray.includes(service.id))
  }

  get serviceName() {
    return this.selectedServices
      .map(service => service.title || service.titleOfTitleField)
      .join(", ")
  }

  get calendarTitle() {
    const bookingPresetModel = new BookingPreset(this.bookingPreset)
    if (String(bookingPresetModel.titleTemplate || '').trim()) {
      return this.booking.calendar_title ||
        this.booking.calendarTitle ||
        this.renderTitleTemplate(bookingPresetModel.titleTemplate) ||
        "(無資料)"
    }
    return this.title
  }

  get title() {
    const bookingPresetModel = new BookingPreset(this.bookingPreset)
    const titleValueMapping = {
      "staff_title": this.staffName,
      "branch_title": this.branchName,
      "booking_number": this.booking.booking_number,
      "service_title": this.serviceName,
    }

    if (bookingPresetModel.titleFieldAlias && bookingPresetModel.titleFieldAlias !== "custom_title") {
      return titleValueMapping[bookingPresetModel.titleFieldAlias] ||
        this.parseValueToString(bookingPresetModel.titleFieldAlias) ||
        "(無資料)"
    }

    return this.booking.customer?.name ||
      this.booking.customer_id ||
      this.booking.booking_number ||
      this.booking.id
  }

  parseValueToString(fieldId) {
    const bookingDataModel = new BookingData(this.booking.data.find(data => data.field_id === fieldId))

    if (bookingDataModel.isCollectionField()) {
      if (bookingDataModel.isArray()) {
        return bookingDataModel.value
          .map((item) => {
            const collectionModel = new Collection(this.collectionsForService.find(collection => collection.id === item.collection_id))
            return collectionModel.textForOption(item.collection_item_id)
          })
          .join(", ")
      }
      const collectionModel = new Collection(this.collectionsForService.find(collection => collection.id === bookingDataModel.value.collection_id))
      return collectionModel.textForOption(bookingDataModel.value.collection_item_id)
    }

    if (bookingDataModel.isArray()) {
      return bookingDataModel.value.join(", ")
    }

    if (isObject(bookingDataModel.value)) {
      return bookingDataModel.value.text
    }

    return bookingDataModel.value
  }

  renderTitleTemplate(template) {
    if (!template) {
      return ""
    }

    return String(template)
      .replace(/\{\{\s*\$booking_data\[['"]([^'"]+)['"]\]\s*\}\}/g, (match, fieldId) => {
        return this.renderBookingDataTemplateValue(fieldId) || ""
      })
      .replace(/\{\{\s*\$customer((?:\[['"][^'"]+['"]\])+)\s*\}\}/g, (match, pathExpression) => {
        return _.get(this.booking.customer, parseBracketPath(pathExpression), "") || ""
      })
      .trim()
  }

  renderBookingDataTemplateValue(fieldId) {
    const specialFieldValueMapping = {
      selectedBranch: this.branchName,
      selectedServices: this.serviceName,
      selectedStaff: this.staffName,
      selectedSchedule: this.booking.start_at,
    }

    if (Object.prototype.hasOwnProperty.call(specialFieldValueMapping, fieldId)) {
      return specialFieldValueMapping[fieldId]
    }

    return this.parseValueToString(fieldId)
  }
}

function isObject(value) {
  return typeof value === 'object' && value !== null;
}

function parseBracketPath(pathExpression) {
  const path = []
  const regex = /\[['"]([^'"]+)['"]\]/g
  let match = regex.exec(pathExpression)

  while (match) {
    path.push(match[1])
    match = regex.exec(pathExpression)
  }

  return path
}
